import { useCallback, useEffect, useId, useState } from 'react';
import { PaintRoller } from 'lucide-react';
import { formatMetersInputValue } from '../lib/floorPlanDesignerDimensions.js';
import {
  DEFAULT_WALL_THICKNESS_M,
  MIN_WALL_LENGTH_METERS,
} from '../lib/floorPlanDesignerState.js';
import FloorPlanEditModal from './FloorPlanEditModal.jsx';
import FloorPlanSegmentedToggle from './FloorPlanSegmentedToggle.jsx';

/** @param {string} raw */
function parseMetersValue(raw) {
  const parsed = Number.parseFloat(String(raw).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @param {{
 *   wall: import('../lib/floorPlanDesignerState.js').DesignerWall;
 *   wallLengthMeters: number;
 *   onApply: (patch: { lengthMeters: number; thicknessMeters: number; visibleIn3D: boolean }) => boolean;
 *   onEditMaterial: () => void;
 *   onClose: () => void;
 * }} props
 */
function FloorPlanWallEditPopup({
  wall,
  wallLengthMeters,
  onApply,
  onEditMaterial,
  onClose,
}) {
  const lengthId = useId();
  const thicknessId = useId();

  const [lengthValue, setLengthValue] = useState(() => formatMetersInputValue(wallLengthMeters));
  const [thicknessValue, setThicknessValue] = useState(() =>
    formatMetersInputValue(wall.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M),
  );
  const [visibleIn3D, setVisibleIn3D] = useState(wall.visibleIn3D ?? true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLengthValue(formatMetersInputValue(wallLengthMeters));
    setThicknessValue(formatMetersInputValue(wall.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M));
    setVisibleIn3D(wall.visibleIn3D ?? true);
    setError('');
  }, [wall, wallLengthMeters]);

  const handleApply = useCallback(() => {
    const lengthM = parseMetersValue(lengthValue);
    const thicknessM = parseMetersValue(thicknessValue);

    if (lengthM === null || lengthM < MIN_WALL_LENGTH_METERS) {
      setError(`Length must be at least ${MIN_WALL_LENGTH_METERS} m.`);
      return;
    }
    if (thicknessM === null || thicknessM <= 0) {
      setError('Thickness must be greater than 0 m.');
      return;
    }

    const applied = onApply({
      lengthMeters: lengthM,
      thicknessMeters: thicknessM,
      visibleIn3D,
    });
    if (!applied) {
      setError('Could not apply wall changes.');
      return;
    }
    onClose();
  }, [lengthValue, thicknessValue, visibleIn3D, onApply, onClose]);

  return (
    <FloorPlanEditModal
      title="Edit wall"
      ariaLabel="Edit wall"
      onClose={onClose}
      onApply={handleApply}
      error={error}
    >
      <div className="floor-plan-opening-edit-fields">
        <div className="floor-plan-edit-modal-field-row">
          <div className="floor-plan-edit-modal-field">
            <label className="floor-plan-wall-length-label" htmlFor={lengthId}>
              Wall length (m)
            </label>
            <input
              id={lengthId}
              type="number"
              min={MIN_WALL_LENGTH_METERS}
              step="0.01"
              className="new-2d-project-input"
              value={lengthValue}
              onChange={(e) => {
                setLengthValue(e.target.value);
                setError('');
              }}
            />
          </div>
          <div className="floor-plan-edit-modal-field">
            <label className="floor-plan-wall-length-label" htmlFor={thicknessId}>
              Wall thickness (m)
            </label>
            <input
              id={thicknessId}
              type="number"
              min={0.01}
              step="0.01"
              className="new-2d-project-input"
              value={thicknessValue}
              onChange={(e) => {
                setThicknessValue(e.target.value);
                setError('');
              }}
            />
          </div>
        </div>

        <div className="floor-plan-edit-modal-field-row">
          <div className="floor-plan-edit-modal-field">
            <span className="floor-plan-wall-length-label">Visible in 3D</span>
            <FloorPlanSegmentedToggle
              value={visibleIn3D}
              options={[
                { value: true, label: 'On' },
                { value: false, label: 'Off' },
              ]}
              onChange={(next) => setVisibleIn3D(!!next)}
              ariaLabel="Visible in 3D"
            />
          </div>
          <div className="floor-plan-edit-modal-field">
            <span className="floor-plan-wall-length-label">Material</span>
            <button type="button" className="btn btn-ghost" onClick={onEditMaterial}>
              <PaintRoller size={16} strokeWidth={2} aria-hidden />
              Edit material
            </button>
          </div>
        </div>
      </div>
    </FloorPlanEditModal>
  );
}

export default FloorPlanWallEditPopup;
